/**
 * Sprint T2P — Timesheet → Payment Lifecycle panel
 *
 * Embedded at the top of `AdminStaffPayments.js` so admins see ONE
 * unified page for the whole booking-payment lifecycle:
 *   1. Pending Approval
 *   2. Ready for Payment  (Pay via Stripe · Mark Paid Externally)
 *   3. Paid               (Stripe transfer OR external — full audit trail)
 *   4. Failed / Needs Attention
 *
 * Counts and rows share the same backend predicate
 * (`/admin/staff-payments/lifecycle`), which pulls from the same SQL
 * helpers as `services/attendance_state.py` — so the Operations
 * Summary tiles and this page can never disagree.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../utils/api';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from './ui/dialog';
import {
  Loader2, Search, DollarSign, CheckCircle2, AlertTriangle,
  Clock, Building2, Calendar, ChevronRight, ExternalLink, History,
  Filter, Download, RotateCcw,
} from 'lucide-react';
import { toast } from 'sonner';

const STAGES = [
  { key: 'pending_approval',  label: 'Pending Approval',       tone: 'bg-amber-100 text-amber-800 border-amber-200' },
  { key: 'ready_for_payment', label: 'Ready for Payment',      tone: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  { key: 'needs_attention',   label: 'Needs Attention',        tone: 'bg-orange-100 text-orange-800 border-orange-200' },
  { key: 'paid',              label: 'Paid',                   tone: 'bg-teal-100 text-teal-800 border-teal-200' },
  { key: 'failed',            label: 'Failed',                 tone: 'bg-rose-100 text-rose-800 border-rose-200' },
];

const EXTERNAL_METHODS = [
  { value: 'cash_app', label: 'Cash App' },
  { value: 'bluevine', label: 'Bluevine' },
  { value: 'stripe',   label: 'Stripe' },
  { value: 'zelle',    label: 'Zelle' },
  { value: 'check',    label: 'Check' },
  { value: 'cash',     label: 'Cash' },
  { value: 'other',    label: 'Other' },
];

const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString() : '—');
const fmtDt = (s) => (s ? new Date(s).toLocaleString() : '—');

export default function AdminStaffPaymentsLifecycle({ embedded = false, defaultStage } = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  // Deep-link: /admin/staff-payments?tab=ready_for_payment (page mode only)
  const initialStage = (() => {
    if (embedded) return defaultStage || 'ready_for_payment';
    const t = new URLSearchParams(location.search).get('tab');
    return STAGES.find((s) => s.key === t) ? t : (defaultStage || 'ready_for_payment');
  })();

  const [stage, setStage] = useState(initialStage);
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [totals, setTotals] = useState({ outstanding_amount: 0, awaiting_talent_count: 0, outstanding_count: 0 });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  // Filters
  const [fClient, setFClient] = useState('');
  const [fMethod, setFMethod] = useState('');
  const [fEventFrom, setFEventFrom] = useState('');
  const [fEventTo, setFEventTo] = useState('');
  const [fApprovedFrom, setFApprovedFrom] = useState('');
  const [fApprovedTo, setFApprovedTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // External-pay dialog
  const [extOpen, setExtOpen] = useState(false);
  const [extRow, setExtRow] = useState(null);
  const [extMethod, setExtMethod] = useState('cash_app');
  const [extRef, setExtRef] = useState('');
  const [extAmount, setExtAmount] = useState('');
  const [extPaidAt, setExtPaidAt] = useState('');
  const [extBusy, setExtBusy] = useState(false);

  // Reopen-for-editing dialog
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reopenRow, setReopenRow] = useState(null);
  const [reopenReason, setReopenReason] = useState('');
  const [reopenBusy, setReopenBusy] = useState(false);

  // Stripe pay confirm
  const [stripeBusyId, setStripeBusyId] = useState(null);

  // Audit drawer
  const [auditOpen, setAuditOpen] = useState(false);
  const [audit, setAudit] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);

  const _filterParams = useCallback((params) => {
    if (q.trim()) params.set('q', q.trim());
    if (fClient.trim()) params.set('client', fClient.trim());
    if (fMethod) params.set('method', fMethod);
    if (fEventFrom) params.set('event_date_from', fEventFrom);
    if (fEventTo) params.set('event_date_to', fEventTo);
    if (fApprovedFrom) params.set('approved_date_from', fApprovedFrom);
    if (fApprovedTo) params.set('approved_date_to', fApprovedTo);
    return params;
  }, [q, fClient, fMethod, fEventFrom, fEventTo, fApprovedFrom, fApprovedTo]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = _filterParams(new URLSearchParams({ stage, limit: '100' }));
      const res = await api.get(`/admin/staff-payments/lifecycle?${params.toString()}`);
      setRows(res.data?.rows || []);
      setCounts(res.data?.counts || {});
      setTotals(res.data?.totals || { outstanding_amount: 0, awaiting_talent_count: 0, outstanding_count: 0 });
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message
        || e?.response?.data?.detail
        || 'Failed to load lifecycle');
    } finally {
      setLoading(false);
    }
  }, [stage, _filterParams]);

  useEffect(() => { load(); }, [stage]);

  // React to ?tab= changes when a tile deep-links here (page mode only)
  useEffect(() => {
    if (embedded) return;
    const t = new URLSearchParams(location.search).get('tab');
    if (t && STAGES.find((s) => s.key === t) && t !== stage) {
      setStage(t);
    }
  }, [location.search]);

  const exportFile = useCallback(async (format) => {
    setExporting(true);
    try {
      const params = _filterParams(new URLSearchParams({ stage, format }));
      const res = await api.get(
        `/admin/staff-payments/lifecycle/export?${params.toString()}`,
        { responseType: 'blob', timeout: 0 },
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `staff_payments_${stage}.${format === 'xlsx' ? 'xlsx' : 'csv'}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${format.toUpperCase()}`);
    } catch (e) {
      toast.error('Export failed');
    } finally { setExporting(false); }
  }, [stage, _filterParams]);

  const openExternalDialog = (row) => {
    setExtRow(row);
    setExtMethod('cash_app');
    setExtRef('');
    const defaultNet = (row.net_amount != null ? row.net_amount : row.approved_amount);
    setExtAmount(defaultNet ? String(Number(defaultNet).toFixed(2)) : '');
    setExtPaidAt(new Date().toISOString().slice(0, 10));
    setExtOpen(true);
  };

  const submitExternal = async () => {
    if (!extRef.trim()) { toast.error('Reference/note is required'); return; }
    setExtBusy(true);
    try {
      const body = { method: extMethod, reference: extRef.trim() };
      if (extAmount) body.amount = parseFloat(extAmount);
      if (extPaidAt) body.paid_at = extPaidAt;
      const res = await api.post(
        `/admin/staff-payments/lifecycle/${extRow.booking_id}/mark-paid-externally`,
        body,
      );
      toast.success(res.data.message || 'Marked paid externally');
      setExtOpen(false);
      await load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((typeof d === 'string' ? d : d?.message) || 'Mark paid failed');
    } finally { setExtBusy(false); }
  };

  const openReopenDialog = (row) => {
    setReopenRow(row);
    setReopenReason('');
    setReopenOpen(true);
  };

  const submitReopen = async () => {
    if (!reopenReason || reopenReason.trim().length < 5) {
      toast.error('Please enter a reason (≥ 5 characters) — required for the audit trail.');
      return;
    }
    setReopenBusy(true);
    try {
      const res = await api.post(
        `/admin/staff-payments/lifecycle/${reopenRow.booking_id}/reopen-approval`,
        { reason: reopenReason.trim() },
      );
      toast.success(res.data?.message || 'Approval reopened');
      setReopenOpen(false);
      await load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((typeof d === 'string' ? d : d?.message) || 'Reopen failed');
    } finally { setReopenBusy(false); }
  };

  const payViaStripe = async (row) => {
    if (!window.confirm(`Send ${fmt(row.approved_amount)} via Stripe to ${row.talent_name}?`)) return;
    setStripeBusyId(row.booking_id);
    // Client-generated idempotency key — safe against double-tap
    const idem = `t2p-${row.booking_id}-${Date.now()}`;
    try {
      const res = await api.post(
        `/admin/staff-payments/lifecycle/${row.booking_id}/pay-via-stripe`,
        { idempotency_key: idem },
      );
      if (res.data?.replayed) {
        toast.info('Idempotent replay — no new charge');
      } else {
        toast.success(res.data?.message || 'Stripe transfer initiated');
      }
      await load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === 'string' ? d : (d?.message || d?.code || 'Stripe pay failed');
      toast.error(msg);
    } finally { setStripeBusyId(null); }
  };

  const openAudit = async (row) => {
    setAuditOpen(true);
    setAudit(null);
    setAuditLoading(true);
    try {
      const res = await api.get(`/admin/staff-payments/lifecycle/${row.booking_id}/audit`);
      setAudit({ row, ...res.data });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load audit');
      setAuditOpen(false);
    } finally { setAuditLoading(false); }
  };

  const recalcAmount = async (row) => {
    if (!window.confirm(
      `Recompute payable amount for ${row.talent_name}?\n\n`
      + `Stored: ${fmt(row.stored_total_pay)}\n`
      + `Live calc: ${fmt(row.computed_amount)} (${row.effective_hours} hrs × ${fmt(row.hourly_rate)}/hr)`
    )) return;
    try {
      const res = await api.post(`/admin/staff-payments/lifecycle/${row.booking_id}/recalc-amount`);
      toast.success(res.data?.message || 'Amount recalculated');
      await load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((typeof d === 'string' ? d : d?.message) || 'Recalculate failed');
    }
  };

  // ─── Booking Detail drawer (Zyrah "arrow" fix) ─────────────────────
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const openDetail = async (row) => {
    setDetailOpen(true);
    setDetail(null);
    setDetailLoading(true);
    try {
      const res = await api.get(`/admin/staff-payments/lifecycle/${row.booking_id}/detail`);
      setDetail(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load booking detail');
      setDetailOpen(false);
    } finally { setDetailLoading(false); }
  };

  // ─── Edit Final Pay Details (Zyrah #2) ─────────────────────────────
  const [editOpen, setEditOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editPreview, setEditPreview] = useState(null);
  const [editBusy, setEditBusy] = useState(false);
  const openEditPay = (row) => {
    setEditRow(row);
    setEditForm({
      clock_in_time:   row.clock_in_time   || '',
      clock_out_time:  row.clock_out_time  || '',
      break_minutes:   row.break_minutes ?? 0,
      hours_override:  row.hours_override  ?? '',
      talent_pay_rate: row.hourly_rate     ?? '',
      reason: '',
    });
    setEditPreview(null);
    setEditOpen(true);
  };
  const submitEdit = async (confirm) => {
    if (!editForm.reason || editForm.reason.trim().length < 5) {
      toast.error('Please enter a reason (≥ 5 characters) — required for the audit trail.');
      return;
    }
    // For safety we always call preview first server-side so the admin
    // has already seen the recomputed amount before confirming. But if
    // Zyrah has already previewed and hasn't changed any field since,
    // clicking Confirm goes directly through.
    setEditBusy(true);
    try {
      const body = { reason: editForm.reason.trim(), confirm };
      if (editForm.clock_in_time)   body.clock_in_time   = editForm.clock_in_time;
      if (editForm.clock_out_time)  body.clock_out_time  = editForm.clock_out_time;
      if (editForm.break_minutes !== '' && editForm.break_minutes !== null)
        body.break_minutes = parseInt(editForm.break_minutes, 10);
      if (editForm.hours_override !== '' && editForm.hours_override !== null && !isNaN(parseFloat(editForm.hours_override)))
        body.hours_override = parseFloat(editForm.hours_override);
      if (editForm.talent_pay_rate !== '' && editForm.talent_pay_rate !== null && !isNaN(parseFloat(editForm.talent_pay_rate)))
        body.talent_pay_rate = parseFloat(editForm.talent_pay_rate);
      const res = await api.post(
        `/admin/staff-payments/lifecycle/${editRow.booking_id}/edit-pay-details`,
        body,
      );
      if (confirm) {
        toast.success(res.data?.message || 'Pay details updated');
        setEditOpen(false);
        await load();
      } else {
        setEditPreview(res.data);
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      const code = d?.code;
      const msg = typeof d === 'string' ? d : (d?.message || 'Edit failed');
      // Surface the actual backend refusal so Zyrah knows why.
      toast.error(code ? `${msg} (${code})` : msg);
    } finally { setEditBusy(false); }
  };

  return (
    <Card className="border-slate-200" data-testid="lifecycle-panel">
      <CardHeader className="pb-2">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <CardTitle className="text-base flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-teal-600" />
            Timesheet → Payment Lifecycle
          </CardTitle>
          <div className="sm:ml-auto flex items-center gap-2">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-slate-400" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && load()}
                placeholder="Search talent, client, event, invoice…"
                className="pl-7 h-8 w-64 text-sm"
                data-testid="lifecycle-search"
              />
            </div>
            <Button size="sm" variant="outline" onClick={() => setShowFilters((v) => !v)} data-testid="lifecycle-filters-toggle">
              <Filter className="w-3.5 h-3.5 mr-1" /> Filters
            </Button>
            <Button size="sm" variant="outline" onClick={load} data-testid="lifecycle-refresh">
              Refresh
            </Button>
            <Button size="sm" variant="outline" onClick={() => exportFile('csv')} disabled={exporting} data-testid="lifecycle-export-csv">
              <Download className="w-3.5 h-3.5 mr-1" /> CSV
            </Button>
            <Button size="sm" variant="outline" onClick={() => exportFile('xlsx')} disabled={exporting} data-testid="lifecycle-export-xlsx">
              <Download className="w-3.5 h-3.5 mr-1" /> Excel
            </Button>
          </div>
        </div>

        {/* Headline totals */}
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2" data-testid="totals-outstanding-amount">
            <div className="text-[10px] uppercase tracking-wider text-emerald-700">Total Outstanding</div>
            <div className="text-lg font-semibold text-emerald-900 tabular-nums">{fmt(totals.outstanding_amount)}</div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2" data-testid="totals-awaiting-count">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Staff Awaiting Payment</div>
            <div className="text-lg font-semibold text-slate-900 tabular-nums">{totals.awaiting_talent_count}</div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2" data-testid="totals-outstanding-count">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Payments Outstanding</div>
            <div className="text-lg font-semibold text-slate-900 tabular-nums">{totals.outstanding_count}</div>
          </div>
        </div>

        {/* Filters panel */}
        {showFilters && (
          <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3 grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="lifecycle-filters-panel">
            <div>
              <Label className="text-[11px]">Client</Label>
              <Input value={fClient} onChange={(e) => setFClient(e.target.value)} placeholder="Client name" className="h-8 text-sm" data-testid="filter-client" />
            </div>
            <div>
              <Label className="text-[11px]">Payment method</Label>
              <select value={fMethod} onChange={(e) => setFMethod(e.target.value)} className="w-full mt-1 border rounded-md px-2 py-1.5 text-sm bg-white" data-testid="filter-method">
                <option value="">Any</option>
                {EXTERNAL_METHODS.map((m) => (<option key={m.value} value={m.value}>{m.label}</option>))}
              </select>
            </div>
            <div className="flex items-end">
              <Button size="sm" onClick={load} className="h-8" data-testid="filter-apply">Apply filters</Button>
              <Button size="sm" variant="ghost" className="h-8 ml-1" data-testid="filter-clear"
                onClick={() => { setFClient(''); setFMethod(''); setFEventFrom(''); setFEventTo(''); setFApprovedFrom(''); setFApprovedTo(''); setTimeout(load, 0); }}>
                Clear
              </Button>
            </div>
            <div>
              <Label className="text-[11px]">Event date from</Label>
              <Input type="date" value={fEventFrom} onChange={(e) => setFEventFrom(e.target.value)} className="h-8 text-sm" data-testid="filter-event-from" />
            </div>
            <div>
              <Label className="text-[11px]">Event date to</Label>
              <Input type="date" value={fEventTo} onChange={(e) => setFEventTo(e.target.value)} className="h-8 text-sm" data-testid="filter-event-to" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[11px]">Approved from</Label>
                <Input type="date" value={fApprovedFrom} onChange={(e) => setFApprovedFrom(e.target.value)} className="h-8 text-sm" data-testid="filter-approved-from" />
              </div>
              <div>
                <Label className="text-[11px]">Approved to</Label>
                <Input type="date" value={fApprovedTo} onChange={(e) => setFApprovedTo(e.target.value)} className="h-8 text-sm" data-testid="filter-approved-to" />
              </div>
            </div>
          </div>
        )}

        {/* Stage tabs (single source of truth: counts + rows same predicate) */}
        <div className="mt-3 flex flex-wrap gap-1.5">
          {STAGES.map((s) => (
            <button
              key={s.key}
              onClick={() => setStage(s.key)}
              data-testid={`lifecycle-tab-${s.key}`}
              className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                stage === s.key
                  ? `${s.tone} shadow-sm`
                  : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
              }`}
            >
              {s.label}
              <span className="ml-1.5 tabular-nums" data-testid={`lifecycle-count-${s.key}`}>
                {counts[s.key] ?? 0}
              </span>
            </button>
          ))}
        </div>
      </CardHeader>

      <CardContent className="p-0">
        {loading ? (
          <div className="p-8 flex items-center justify-center text-slate-500">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-slate-500 text-sm" data-testid="lifecycle-empty">
            No records in this stage.
          </div>
        ) : (
          <div className="divide-y">
            {rows.map((r) => (
              <LifecycleRow
                key={r.booking_id}
                r={r}
                stage={stage}
                stripeBusyId={stripeBusyId}
                onPayStripe={payViaStripe}
                onMarkExternal={openExternalDialog}
                onOpenAudit={openAudit}
                onOpenDetail={openDetail}
                onEditPay={openEditPay}
                onRecalcAmount={recalcAmount}
                onReopen={openReopenDialog}
              />
            ))}
          </div>
        )}
      </CardContent>

      {/* ─── Mark Paid Externally dialog ─── */}
      <Dialog open={extOpen} onOpenChange={setExtOpen}>
        <DialogContent className="max-w-md bg-white" data-testid="ext-pay-dialog">
          <DialogHeader>
            <DialogTitle>Mark Paid Externally</DialogTitle>
            <DialogDescription>
              {extRow && (
                <span>Recording an out-of-Stripe payment for <b>{extRow.talent_name}</b> · {extRow.event_name} · {fmtDate(extRow.shift_date)}.</span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Payment method</Label>
              <select
                value={extMethod}
                onChange={(e) => setExtMethod(e.target.value)}
                className="w-full mt-1 border rounded-md px-2 py-1.5 text-sm"
                data-testid="ext-method-select"
              >
                {EXTERNAL_METHODS.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Reference / Note <span className="text-rose-600">*</span></Label>
              <Textarea
                value={extRef}
                onChange={(e) => setExtRef(e.target.value)}
                placeholder="Transaction ID, check number, screenshot ID — required for audit."
                rows={3}
                data-testid="ext-ref-input"
              />
            </div>
            <div>
              <Label>Amount (net payable)</Label>
              {extRow && extRow.oa_deduction > 0 && (
                <div className="text-[11px] text-slate-600 mb-1 rounded bg-slate-50 border border-slate-200 px-2 py-1" data-testid="ext-oa-breakdown">
                  Gross {fmt(extRow.gross_amount ?? extRow.approved_amount)}
                  {'  −  '}OA insurance {fmt(extRow.oa_deduction)} ({fmt(extRow.oa_rate_per_hour)}/hr)
                  {'  =  '}<b className="text-emerald-800">Net {fmt(extRow.net_amount)}</b>
                </div>
              )}
              <Input
                type="number"
                value={extAmount}
                onChange={(e) => setExtAmount(e.target.value)}
                step="0.01"
                data-testid="ext-amount-input"
              />
              <p className="text-[11px] text-slate-500 mt-1">
                Defaults to the <b>net payable</b> (after OA insurance). This is what the contractor receives.
              </p>
            </div>
            <div>
              <Label>Payment date</Label>
              <Input
                type="date"
                value={extPaidAt}
                onChange={(e) => setExtPaidAt(e.target.value)}
                data-testid="ext-paid-at-input"
              />
              <p className="text-[11px] text-slate-500 mt-1">
                When the money actually moved (defaults to today). Back-date if needed.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExtOpen(false)} disabled={extBusy}>Cancel</Button>
            <Button onClick={submitExternal} disabled={extBusy || !extRef.trim()} data-testid="ext-submit-btn">
              {extBusy && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />}
              Mark Paid
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Reopen for Editing dialog ─── */}
      <Dialog open={reopenOpen} onOpenChange={setReopenOpen}>
        <DialogContent className="max-w-md bg-white" data-testid="reopen-dialog">
          <DialogHeader>
            <DialogTitle>Reopen for Editing</DialogTitle>
            <DialogDescription>
              {reopenRow && (
                <span>Move <b>{reopenRow.talent_name}</b>'s approved timesheet ({reopenRow.event_name} · {fmtDate(reopenRow.shift_date)}) back to Pending Approval so the hours/pay can be corrected. It must be approved again before payment.</span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Reason <span className="text-rose-600">*</span></Label>
            <Textarea
              value={reopenReason}
              onChange={(e) => setReopenReason(e.target.value)}
              placeholder="Why this timesheet is being reopened (audit trail)"
              rows={3}
              data-testid="reopen-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReopenOpen(false)} disabled={reopenBusy}>Cancel</Button>
            <Button onClick={submitReopen} disabled={reopenBusy || reopenReason.trim().length < 5} data-testid="reopen-submit-btn">
              {reopenBusy && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />}
              Reopen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Audit drawer ─── */}
      <Dialog open={auditOpen} onOpenChange={setAuditOpen}>
        <DialogContent className="max-w-2xl bg-white max-h-[85vh] overflow-y-auto" data-testid="audit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-4 h-4" /> Payment lifecycle · Audit
            </DialogTitle>
            <DialogDescription className="sr-only">Booking audit history</DialogDescription>
          </DialogHeader>
          {auditLoading || !audit ? (
            <div className="py-10 flex items-center justify-center text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          ) : (
            <div className="space-y-4 text-sm" data-testid="audit-body">
              <div className="rounded border p-3 bg-slate-50">
                <div className="font-semibold">{audit.row.talent_name}</div>
                <div className="text-xs text-slate-600">
                  {audit.row.event_name} · {audit.row.client_name} · {fmtDate(audit.row.shift_date)}
                </div>
                <div className="text-xs mt-1">
                  {audit.booking.hours_worked ?? '—'} hrs
                  {audit.booking.hours_override != null && (
                    <> · adjusted to {audit.booking.hours_override} hrs</>
                  )}
                  {' · '}rate {fmt(audit.booking.hourly_rate)} · total {fmt(audit.booking.total_pay)}
                </div>
              </div>

              <div>
                <div className="font-semibold mb-1 flex items-center gap-1">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> Timesheet
                </div>
                <ul className="text-xs space-y-0.5">
                  {audit.booking.timesheet_approved_at && (
                    <li>Approved {fmtDt(audit.booking.timesheet_approved_at)} by {audit.booking.timesheet_approved_by || '—'}</li>
                  )}
                  {audit.booking.timesheet_rejected_at && (
                    <li className="text-rose-700">Rejected {fmtDt(audit.booking.timesheet_rejected_at)} by {audit.booking.timesheet_rejected_by || '—'} — {audit.booking.rejection_reason || 'no reason'}</li>
                  )}
                  {audit.booking.notes && <li>Notes: {audit.booking.notes}</li>}
                </ul>
              </div>

              <div>
                <div className="font-semibold mb-1 flex items-center gap-1">
                  <DollarSign className="w-3.5 h-3.5 text-teal-600" /> Payouts ({audit.payouts.length})
                </div>
                {audit.payouts.length === 0 ? (
                  <p className="text-xs text-slate-500">No payout attempts yet.</p>
                ) : (
                  <ul className="text-xs space-y-1">
                    {audit.payouts.map((p) => (
                      <li key={p.payout_id} className="border-l-2 border-slate-200 pl-2 py-0.5">
                        <div>
                          <b>{p.settled_externally ? `External · ${p.settled_via}` : (p.stripe_transfer_id ? 'Stripe transfer' : 'Stripe attempt')}</b>
                          {' — '}{fmt(p.amount)}{' · '}
                          <Badge className="text-[10px]" data-testid={`audit-status-${p.payout_id}`}>{p.status}</Badge>
                        </div>
                        <div className="text-slate-500">
                          Created {fmtDt(p.created_at)}
                          {p.completed_at && <> · Completed {fmtDt(p.completed_at)}</>}
                          {p.settled_by && <> · by {p.settled_by}</>}
                        </div>
                        {p.settled_notes && <div className="text-slate-600 italic">Note: {p.settled_notes}</div>}
                        {p.failed_reason && <div className="text-rose-700">Failed: {p.failed_reason}</div>}
                        {p.stripe_transfer_id && <div className="text-[10px] text-slate-500">tr_id: <code>{p.stripe_transfer_id}</code></div>}
                        {p.idempotency_key && <div className="text-[10px] text-slate-400">idem: <code>{p.idempotency_key.slice(0, 32)}…</code></div>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {audit.activity.length > 0 && (
                <div>
                  <div className="font-semibold mb-1">Activity log</div>
                  <ul className="text-xs space-y-0.5">
                    {audit.activity.map((a, i) => (
                      <li key={i} className="text-slate-600">
                        <span className="text-slate-400">{fmtDt(a.created_at)}</span> · {a.action} · {a.actor || '—'}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* ─── Booking Detail drawer (arrow → detail) ─── */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-3xl bg-white max-h-[85vh] overflow-y-auto" data-testid="detail-dialog">
          <DialogHeader>
            <DialogTitle>Booking Detail — {detail?.talent?.name}</DialogTitle>
            <DialogDescription>
              {detail?.event?.name} · {fmtDate(detail?.event?.shift_date)}
            </DialogDescription>
          </DialogHeader>
          {detailLoading || !detail ? (
            <div className="py-10 flex items-center justify-center text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          ) : (
            <div className="text-sm grid gap-3" data-testid="detail-body">
              <DetailBlock title="Talent" items={[
                ['Name',  detail.talent.name],
                ['Email', detail.talent.email],
                ['Role',  detail.event.role],
              ]} />
              <DetailBlock title="Shift" items={[
                ['Event',      detail.event.name],
                ['Venue',      detail.event.venue_name],
                ['Date',       fmtDate(detail.event.shift_date)],
                ['Scheduled',  `${detail.event.scheduled?.start_time || '—'} – ${detail.event.scheduled?.end_time || '—'}`],
                ['Client',     detail.client?.name],
              ]} />
              <DetailBlock title="Timesheet" items={[
                ['Clock in',     fmtDt(detail.timesheet.clock_in_time)],
                ['Clock out',    fmtDt(detail.timesheet.clock_out_time)],
                ['Break',        `${detail.timesheet.break_minutes} min`],
                ['Hours worked', detail.timesheet.hours_worked],
                ['Approved hrs', `${detail.timesheet.effective_hours}${detail.timesheet.hours_approved ? ' (locked)' : ''}`],
                ['Regular / OT', `${detail.timesheet.regular_hours || 0} / ${detail.timesheet.overtime_hours || 0} × ${detail.timesheet.overtime_multiplier || 1.5}`],
                ['Approved by',  `${detail.timesheet.timesheet_approved_by || '—'} · ${fmtDt(detail.timesheet.timesheet_approved_at)}`],
                detail.timesheet.timesheet_notes ? ['Notes', detail.timesheet.timesheet_notes] : null,
              ].filter(Boolean)} />
              <DetailBlock title="Pay" items={[
                ['Rate',           `${fmt(detail.pay.effective_rate)} (${detail.pay.rate_source})`],
                ['Breakdown',      detail.pay.breakdown],
                ['Stored',         fmt(detail.pay.stored_total_pay)],
                ['Live computed',  fmt(detail.pay.computed_amount)],
                ['Payment status', detail.pay.payment_status],
                detail.pay.has_mismatch ? ['⚠️  Mismatch', 'Stored differs from live calc — click Recalculate on the row.'] : null,
              ].filter(Boolean)} />
              {detail.payouts.length > 0 && (
                <div className="border rounded p-3">
                  <div className="font-semibold text-sm mb-1">Payouts ({detail.payouts.length})</div>
                  <ul className="text-xs space-y-1">
                    {detail.payouts.map((p) => (
                      <li key={p.id}>
                        <b>{p.settled_externally ? `External · ${p.settled_via}` : (p.stripe_transfer_id ? 'Stripe' : 'Attempt')}</b>{' '}
                        {fmt(p.amount)} · <Badge className="text-[10px]">{p.status}</Badge>
                        {' · '}{fmtDt(p.created_at)}{p.settled_by && <> · by {p.settled_by}</>}
                        {p.failed_reason && <div className="text-rose-700">{p.failed_reason}</div>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {detail.adjustment_history.length > 0 && (
                <div className="border rounded p-3">
                  <div className="font-semibold text-sm mb-1">Adjustments / Approval History</div>
                  <ul className="text-xs space-y-0.5">
                    {detail.adjustment_history.map((a, i) => (
                      <li key={i} className="text-slate-600">
                        <span className="text-slate-400">{fmtDt(a.created_at)}</span> · {a.action} · {a.actor || '—'}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ─── Edit Final Pay Details (Zyrah #2) ─── */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg bg-white max-h-[90vh] overflow-y-auto" data-testid="edit-pay-dialog">
          <DialogHeader>
            <DialogTitle>Edit Final Pay Details</DialogTitle>
            <DialogDescription>
              Adjust clock times, hours, break, OT split, or rate for{' '}
              <b>{editRow?.talent_name}</b> · {fmtDate(editRow?.shift_date)}.
              All changes recompute the payable amount automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Clock in</Label>
                <Input type="datetime-local"
                  value={editForm.clock_in_time ? editForm.clock_in_time.slice(0, 16) : ''}
                  onChange={(e) => setEditForm({ ...editForm, clock_in_time: e.target.value })}
                  data-testid="edit-clock-in" />
              </div>
              <div>
                <Label>Clock out</Label>
                <Input type="datetime-local"
                  value={editForm.clock_out_time ? editForm.clock_out_time.slice(0, 16) : ''}
                  onChange={(e) => setEditForm({ ...editForm, clock_out_time: e.target.value })}
                  data-testid="edit-clock-out" />
              </div>
              <div>
                <Label>Break (min)</Label>
                <Input type="number" min="0"
                  value={editForm.break_minutes ?? ''}
                  onChange={(e) => setEditForm({ ...editForm, break_minutes: e.target.value })}
                  data-testid="edit-break" />
              </div>
              <div>
                <Label>Hours override</Label>
                <Input type="number" step="0.01" min="0"
                  value={editForm.hours_override ?? ''}
                  onChange={(e) => setEditForm({ ...editForm, hours_override: e.target.value })}
                  data-testid="edit-hours" />
              </div>
              <div>
                <Label>Talent pay rate ($/hr)</Label>
                <Input type="number" step="0.01" min="0"
                  value={editForm.talent_pay_rate ?? ''}
                  onChange={(e) => setEditForm({ ...editForm, talent_pay_rate: e.target.value })}
                  data-testid="edit-rate" />
              </div>
            </div>
            <div>
              <Label>Reason <span className="text-rose-600">*</span></Label>
              <Textarea rows={2}
                value={editForm.reason}
                onChange={(e) => setEditForm({ ...editForm, reason: e.target.value })}
                placeholder="Why this correction is needed (audit trail)"
                data-testid="edit-reason" />
            </div>
            {editPreview && editPreview.preview && (
              <div className="rounded border p-2 bg-emerald-50 border-emerald-200 text-xs">
                <div className="font-semibold text-emerald-900">Preview</div>
                <div>Previous: {fmt(editPreview.previous.total_pay)} ·{' '}
                     {editPreview.previous.regular_hours ?? 0}h reg + {editPreview.previous.overtime_hours ?? 0}h OT</div>
                <div>New: <b>{fmt(editPreview.proposed.total_pay)}</b> ·{' '}
                     {editPreview.proposed.regular_hours}h reg + {editPreview.proposed.overtime_hours}h OT</div>
                <div className="mt-1 text-emerald-800">{editPreview.proposed.breakdown}</div>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEditOpen(false)} disabled={editBusy}>Cancel</Button>
            <Button
              variant="outline"
              onClick={() => submitEdit(false)}
              disabled={editBusy || !editForm.reason || editForm.reason.trim().length < 5}
              data-testid="edit-preview-btn"
              title={!editForm.reason || editForm.reason.trim().length < 5 ? 'Enter a reason (≥ 5 chars) first' : 'See the recomputed amount without saving'}
            >
              {editBusy && !editPreview?.confirmed && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />}
              Preview
            </Button>
            <Button
              onClick={() => submitEdit(true)}
              disabled={editBusy || !editForm.reason || editForm.reason.trim().length < 5}
              data-testid="edit-confirm-btn"
              title={!editForm.reason || editForm.reason.trim().length < 5 ? 'Enter a reason (≥ 5 chars) first' : 'Save changes and freshen the payable amount'}
            >
              {editBusy && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />}
              Confirm &amp; Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}


function DetailBlock({ title, items }) {
  return (
    <div className="border rounded p-3">
      <div className="font-semibold text-sm mb-1">{title}</div>
      <dl className="text-xs grid grid-cols-[8rem_1fr] gap-x-2 gap-y-0.5">
        {items.map(([k, v], i) => (
          <React.Fragment key={i}>
            <dt className="text-slate-500">{k}</dt>
            <dd className="text-slate-800">{v ?? '—'}</dd>
          </React.Fragment>
        ))}
      </dl>
    </div>
  );
}


function LifecycleRow({ r, stage, stripeBusyId, onPayStripe, onMarkExternal, onOpenAudit, onOpenDetail, onEditPay, onRecalcAmount, onReopen }) {
  const externallySettled = r.latest_payout?.settled_externally;
  const paidLabel = externallySettled
    ? `Paid externally · ${r.latest_payout.settled_via || 'unknown'}`
    : (r.latest_payout?.status === 'completed' ? 'Paid via Stripe' : '');
  const hasBlockers = Array.isArray(r.pay_blockers) && r.pay_blockers.length > 0;
  const canPay = r.payable !== false && !hasBlockers && !externallySettled;

  return (
    <div className="px-4 py-3 hover:bg-slate-50" data-testid={`lifecycle-row-${r.booking_id}`}>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-slate-900" data-testid="lifecycle-talent">{r.talent_name}</span>
            {r.role_name && (
              <Badge className="text-[10px] bg-slate-100 text-slate-700 border-slate-200">{r.role_name}</Badge>
            )}
            {externallySettled && (
              <Badge className="text-[10px] bg-teal-100 text-teal-800 border-teal-200">
                External settlement
              </Badge>
            )}
            {r.pay_blockers?.includes('financial_mismatch') && (
              <Badge className="text-[10px] bg-rose-100 text-rose-800 border-rose-200"
                     data-testid={`badge-mismatch-${r.booking_id}`}>
                Financial mismatch
              </Badge>
            )}
            {r.pay_blockers?.includes('hours_missing') && (
              <Badge className="text-[10px] bg-amber-100 text-amber-800 border-amber-200"
                     data-testid={`badge-hours-missing-${r.booking_id}`}>
                Hours missing
              </Badge>
            )}
            {r.pay_blockers?.includes('rate_missing') && (
              <Badge className="text-[10px] bg-amber-100 text-amber-800 border-amber-200"
                     data-testid={`badge-rate-missing-${r.booking_id}`}>
                Rate missing
              </Badge>
            )}
          </div>
          <div className="text-xs text-slate-600 mt-0.5 flex items-center gap-2 flex-wrap">
            <Building2 className="w-3 h-3" /> {r.client_name || '—'}
            <span>·</span>
            <span>{r.event_name || '—'}</span>
            <span>·</span>
            <Calendar className="w-3 h-3" /> {fmtDate(r.shift_date)}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            {(r.effective_hours ?? r.hours_worked) ?? '—'} hrs
            {r.hours_override != null && <> (adj. {r.hours_override})</>}
            {r.hourly_rate ? ` · ${fmt(r.hourly_rate)}/hr` : ''}
            {r.rate_source === 'shift' && (
              <span className="ml-1 text-[10px] text-slate-400">(shift rate fallback)</span>
            )}
            {' · '}
            {r.oa_deduction > 0 ? (
              <span data-testid={`pay-breakdown-${r.booking_id}`}>
                <span className="text-slate-400 line-through mr-1">{fmt(r.gross_amount ?? r.approved_amount)}</span>
                <b className={r.has_financial_mismatch ? 'text-rose-700' : 'text-emerald-800'}>{fmt(r.net_amount)}</b>
                <span className="ml-1 text-[10px] text-slate-400">net (− OA ins. {fmt(r.oa_deduction)})</span>
              </span>
            ) : (
              <b className={r.has_financial_mismatch ? 'text-rose-700' : 'text-slate-800'} data-testid={`pay-breakdown-${r.booking_id}`}>{fmt(r.net_amount ?? r.approved_amount)}</b>
            )}
            {r.has_financial_mismatch && (
              <span className="ml-1 text-rose-700">
                (stored {fmt(r.stored_total_pay)})
              </span>
            )}
            {r.invoice_number && <> · inv {r.invoice_number}</>}
          </div>
          {paidLabel && (
            <div className="text-xs text-teal-700 mt-0.5" data-testid={`paid-info-${r.booking_id}`}>
              {paidLabel} · {fmtDt(r.latest_payout?.completed_at || r.latest_payout?.settled_at)}
              {r.latest_payout?.settled_notes && (
                <span className="text-slate-500"> · Ref: {r.latest_payout.settled_notes}</span>
              )}
            </div>
          )}
          {hasBlockers && !externallySettled && (
            <div className="text-xs text-rose-700 mt-1" data-testid={`blocker-msg-${r.booking_id}`}>
              Payment blocked: {r.pay_blockers.join(', ')} — correct the timesheet
              {r.has_financial_mismatch && <> or click Recalculate</>}
              {' '}before paying.
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-1.5 sm:justify-end shrink-0">
          {(stage === 'ready_for_payment' || stage === 'needs_attention') && !externallySettled && (
            <>
              <Button
                size="sm"
                onClick={() => onPayStripe(r)}
                disabled={!canPay || stripeBusyId === r.booking_id}
                data-testid={`pay-stripe-${r.booking_id}`}
                title={!canPay ? 'Payment blocked — see badges' : ''}
              >
                {stripeBusyId === r.booking_id ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <DollarSign className="w-3.5 h-3.5 mr-1" />}
                Pay via Stripe
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onMarkExternal(r)}
                disabled={!canPay}
                data-testid={`mark-external-${r.booking_id}`}
                title={!canPay ? 'Payment blocked — see badges' : ''}
              >
                Mark Paid Externally
              </Button>
              {r.has_financial_mismatch && (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => onRecalcAmount(r)}
                  data-testid={`recalc-${r.booking_id}`}
                >
                  Recalculate
                </Button>
              )}
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onEditPay(r)}
                data-testid={`edit-pay-${r.booking_id}`}
                title="Edit Final Pay Details before payment"
              >
                Edit Pay
              </Button>
              {onReopen && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => onReopen(r)}
                  data-testid={`reopen-${r.booking_id}`}
                  title="Reopen this approved timesheet for editing"
                >
                  <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reopen for Editing
                </Button>
              )}
            </>
          )}
          {stage === 'failed' && !externallySettled && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onMarkExternal(r)}
              data-testid={`failed-mark-external-${r.booking_id}`}
            >
              Mark Paid Externally
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onOpenAudit(r)}
            data-testid={`open-audit-${r.booking_id}`}
          >
            <History className="w-3.5 h-3.5 mr-1" /> Audit
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onOpenDetail(r)}
            data-testid={`open-detail-${r.booking_id}`}
            title="Open booking detail"
          >
            <ExternalLink className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
